/**
 * Location Utilities
 *
 * Reusable helpers for multi-location support.
 * Single-location installers (locations.length <= 1) see no location UI.
 */

// Sentinel value for "All Locations" view
export const ALL_LOCATIONS_ID = 'all';

/**
 * Get the location object for the currently selected location
 * @param {Array} locations - Array of location objects
 * @param {string|number} selectedLocationId - Currently selected location ID
 * @returns {object|null} Location object or null if "All" or not found
 */
export function getSelectedLocation(locations, selectedLocationId) {
  if (!locations || !selectedLocationId || selectedLocationId === ALL_LOCATIONS_ID) return null;
  return locations.find(l => String(l.location_id) === String(selectedLocationId)) || null;
}

/**
 * Read selectedLocationId from localStorage companyAPI
 * @returns {string|number|null}
 */
export function getSelectedLocationId() {
  try {
    const company = JSON.parse(localStorage.getItem('companyAPI'));
    return company?.selectedLocationId || null;
  } catch {
    return null;
  }
}

/**
 * Read locations array from localStorage companyAPI
 * @returns {Array}
 */
export function getLocations() {
  try {
    const company = JSON.parse(localStorage.getItem('companyAPI'));
    return company?.locations || [];
  } catch {
    return [];
  }
}

/**
 * Check if the installer has multiple locations
 * @param {Array} locations - Array of location objects
 * @returns {boolean}
 */
export function isMultiLocation(locations) {
  return Array.isArray(locations) && locations.length > 1;
}

/**
 * Check if "All Locations" is currently selected
 * @param {string|number} selectedLocationId
 * @returns {boolean}
 */
export function isAllLocations(selectedLocationId) {
  return !selectedLocationId || selectedLocationId === ALL_LOCATIONS_ID;
}

/**
 * Mobile-mode helpers — `installer_locations.installer_type` is 'fixed' (default) or 'mobile'.
 * Per server spec MOBILE_LOCATION_SERVER_SPEC.md.
 */
export function getInstallerType(location) {
  return location?.installer_type === 'mobile' ? 'mobile' : 'fixed';
}

export function isMobileLocation(location) {
  return getInstallerType(location) === 'mobile';
}

/**
 * Return `{ lat, lng }` for the location's base position, or null.
 * Server stores these as flat `base_lat`/`base_lng` columns on
 * `installer_locations` (per MOBILE_LOCATION_SERVER_SPEC).
 */
export function getBaseCoords(location) {
  if (!location) return null;
  const lat = location.base_lat;
  const lng = location.base_lng;
  if (lat == null || lng == null) return null;
  return { lat: Number(lat), lng: Number(lng) };
}

/**
 * Get a display-friendly name for a location
 * @param {object} location - Location object
 * @returns {string}
 */
export function getLocationName(location) {
  if (!location) return '';
  if (location.location_name) return location.location_name;
  const parts = [location.address_city, location.address_state].filter(Boolean);
  return parts.join(', ') || `Location ${location.location_id}`;
}

// Consistent color palette for location badges and calendar color-coding
const LOCATION_COLORS = [
  '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', // theme-ignore-line (per-location identity colours, approved)
  '#6f42c1', '#fd7e14', '#20c997', '#e83e8c', '#6610f2' // theme-ignore-line (per-location identity colours, approved)
];

/**
 * Get a consistent color for a location (for badges, calendar entries)
 * @param {string|number} locationId
 * @param {Array} locations - Full locations array (for index-based color assignment)
 * @returns {string} Hex color
 */
export function getLocationBadgeColor(locationId, locations) {
  if (!locations || !locationId) return LOCATION_COLORS[0];
  const index = locations.findIndex(l => String(l.location_id) === String(locationId));
  return LOCATION_COLORS[(index >= 0 ? index : 0) % LOCATION_COLORS.length];
}

// --- Dealer Group Utilities ---
// Mirror the location utilities for dealer group → dealership scoping

// Sentinel value for "All Dealerships" view
export const ALL_DEALERSHIPS_ID = 'all';

/**
 * Read selectedDealerId from localStorage dealerGroupAPI
 * @returns {string|number} 'all' = all dealerships, or a specific dealer_id
 */
export function getSelectedDealerId() {
  try {
    const dgInfo = JSON.parse(localStorage.getItem('dealerGroupAPI'));
    if (!dgInfo) return ALL_DEALERSHIPS_ID;
    const val = dgInfo.selectedDealerId;
    if (val === undefined || val === null) return ALL_DEALERSHIPS_ID;
    // Legacy: 0 was the previous "all" sentinel — coerce to 'all'
    if (val === 0 || val === '0') return ALL_DEALERSHIPS_ID;
    return val;
  } catch {
    return ALL_DEALERSHIPS_ID;
  }
}

/**
 * Check if viewing all dealerships
 * @param {string|number} selectedDealerId
 * @returns {boolean}
 */
export function isAllDealerships(selectedDealerId) {
  // Tolerate legacy 0/'0' for one release in case stale localStorage still holds the old sentinel.
  return !selectedDealerId
    || selectedDealerId === ALL_DEALERSHIPS_ID
    || selectedDealerId === 0
    || selectedDealerId === '0';
}

/**
 * Check if the dealer group has multiple dealerships
 * @param {Array} dealerships - Array of dealership objects
 * @returns {boolean}
 */
export function isMultiDealership(dealerships) {
  return Array.isArray(dealerships) && dealerships.length > 1;
}

/**
 * Get a consistent color for a dealership badge
 * @param {string|number} dealerId
 * @param {Array} dealerships - Full dealerships array
 * @returns {string} Hex color
 */
export function getDealershipBadgeColor(dealerId, dealerships) {
  if (!dealerships || !dealerId) return LOCATION_COLORS[0];
  const index = dealerships.findIndex(d => String(d.dealer_id) === String(dealerId));
  return LOCATION_COLORS[(index >= 0 ? index : 0) % LOCATION_COLORS.length];
}
